import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sentado from './images/sentado.png';

const ThankYouScreen = () => {
  const [waveActive, setWaveActive] = useState(false);
  const [letters, setLetters] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Cerrar sesión real al llegar a esta página
    const closeSession = () => {
      // Eliminar el token del localStorage
      localStorage.removeItem('token');
      // También eliminar cualquier otra información de sesión si existe
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      
      // Opcional: Hacer una llamada al backend para invalidar el token
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_API_URL}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {
          // Ignorar errores en el logout del backend
        });
      }
    };

    // Ejecutar el cierre de sesión
    closeSession();

    // Dividir el texto en letras manteniendo los espacios
    setLetters("¡Muchas gracias!".split(''));
    
    // Activar el efecto de onda después de 1 segundo
    const timer = setTimeout(() => {
      setWaveActive(true);
    }, 100);

    const fadeTimer = setTimeout(() => {
      setIsLoaded(true);
    }, 60);

    // Redirigir automáticamente al login después de 8 segundos
    const redirectTimer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        navigate('/');
      }, 700);
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fadeTimer);
      clearTimeout(redirectTimer);
    };
  }, [navigate]);

  // Función para crear la animación de onda
  const getWaveAnimation = (index: number) => {
    if (!waveActive) return {};
    
    return {
      animation: `wave 250ms ease-in-out ${index * 70}ms forwards`,
      '@keyframes wave': {
        '0%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-20px)' },
        '100%': { transform: 'translateY(0px)' }
      }
    };
  };

  return (
    <>
      {/* VERSIÓN MÓVIL */}
      <div
        className={`block sm:hidden min-h-screen bg-white font-futura flex flex-col items-start justify-start px-6 pt-4 pb-4 relative transition-all duration-700 ease-in-out ${isExiting ? 'opacity-0' : isLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ minHeight: '100vh', pointerEvents: isLoaded && !isExiting ? 'auto' : 'none' }}
      >
        <div className="flex flex-col justify-center items-start w-full flex-1">
          <h1 className="text-4xl font-bold text-gray-800 text-left leading-tight mb-2 mt-0 w-full flex flex-wrap" style={{ letterSpacing: '0.01em', lineHeight: 1.1 }}>
            {"¡Muchas gracias!".split('').map((letter, index) => (
              <span
                key={index}
                className="inline-block"
                style={{
                  animation: waveActive ? `wave 250ms ease-in-out ${index * 70}ms forwards` : 'none',
                  whiteSpace: letter === ' ' ? 'pre' : 'normal'
                }}
              >
                {letter}
              </span>
            ))}
          </h1>
          <p className="mb-4 text-lg text-gray-800 font-normal text-left w-full" style={{ letterSpacing: '0.01em' }}>
            Ahora nos encargamos nosotros
          </p>
          <div className="w-full flex flex-col items-center justify-center">
            <img
              src={sentado}
              alt="Persona sentada usando un teléfono"
              className="w-[95%] max-w-md mb-2 mt-2"
              style={{ objectFit: 'contain', minHeight: 220, maxHeight: 340, opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.7s' }}
              onLoad={() => setImageLoaded(true)}
            />
            <footer className="w-full text-center text-gray-400 text-xs tracking-widest mt-2 mb-0">
              COPYRIGHT © 2025 SIWEB
            </footer>
          </div>
        </div>
        {/* Estilos CSS para la animación de onda */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes wave {
              0% { transform: translateY(0px); }
              50% { transform: translateY(-20px); }
              100% { transform: translateY(0px); }
            }
          `
        }} />
      </div>

      {/* VERSIÓN ESCRITORIO */}
      <div className="hidden sm:block">
        <div className="relative min-h-screen bg-white font-futura overflow-hidden">
          {/* Fondos con degradado */}
          <div className="absolute top-1/2 left-1/4 w-1/2 h-1/2 -translate-y-1/2 bg-yellow-100/30 rounded-full blur-3xl filter opacity-50"></div>
          <div className="absolute top-1/2 left-1/3 w-1/2 h-1/2 -translate-y-1/2 bg-blue-200/30 rounded-full blur-3xl filter opacity-40"></div>
          {/* Efecto de brillo azul adicional */}
          <div className="absolute top-1/2 left-[60%] transform -translate-x-1/2 -translate-y-1/2 w-1/3 h-1/3 bg-blue-300/40 rounded-full blur-3xl filter opacity-60"></div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center w-full min-h-screen"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(48px)',
              transition: 'opacity 0.7s cubic-bezier(0.4,0,0.2,1), transform 0.7s cubic-bezier(0.4,0,0.2,1)'
            }}
          >
            {/* Sección de texto */}
            <div className="text-left lg:w-5/12 xl:w-1/2 order-1 lg:order-none px-12 md:px-40 py-10 lg:py-0">
              <h1 className="text-6xl md:text-7xl font-bold text-gray-800 flex flex-wrap" style={{ letterSpacing: '0.05em' }}>
                {letters.map((letter, index) => (
                  <span
                    key={index}
                    className="inline-block"
                    style={{
                      animation: waveActive ? `wave 250ms ease-in-out ${index * 70}ms forwards` : 'none',
                      whiteSpace: letter === ' ' ? 'pre' : 'normal'
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </h1>
              <p className="mt-4 text-2xl text-gray-800 font-bold">
                Ahora nos encargamos nosotros
              </p>
            </div>

            {/* Sección de imagen */}
            <div className="lg:w-7/12 xl:w-1/2 order-0 lg:order-none flex justify-center items-center mt-10 lg:mt-0 lg:-translate-x-16">
              <img
                src={sentado}
                alt="Persona sentada usando un teléfono"
                className="w-full h-auto max-w-[52rem] transition-opacity duration-700"
                style={{ opacity: imageLoaded ? 1 : 0 }}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          </div>

          {/* Footer */}
          <footer className="absolute bottom-4 w-full text-center text-gray-500 text-xs tracking-widest">
            COPYRIGHT © 2025 SIWEB
          </footer>

          {/* Estilos CSS para la animación de onda */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes wave {
                0% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-20px);
                }
                100% {
                  transform: translateY(0px);
                }
              }
            `
          }} />
        </div>
      </div>
    </>
  );
};

export default ThankYouScreen; 